// Page Emplacements du dashboard (décisions 8 et 45, maquette D5) : tout ce qui s'est passé sur un
// hébergement, interventions, inventaires et tour de terrain. Logique pure, testée à part.
// Planning : Inventory/PLANNING-INVENTAIRES-STAFF.md

import { pitchKind } from '../Inventory/Field/fieldSearch'
import { tourZones, entriesAt, stateOfEntries, TOUR_STATES } from '../Inventory/Field/fieldTours'

// Statuts du module Interventions (Interventions/types.js) : tout sauf DONE reste ouvert
export const OPEN_STATUSES = ['TODO', 'IN_PROGRESS', 'BLOCKED']

export const STATUS_LABELS = {
	TODO: 'À faire',
	IN_PROGRESS: 'En cours',
	BLOCKED: 'Bloquée',
	DONE: 'Terminée'
}

export function isOpenIntervention(intervention)
{
	return OPEN_STATUSES.includes(intervention.status)
}

// Le module Interventions rattache une intervention à l'_id de l'emplacement (location_id)
export function interventionsByPitch(interventions)
{
	const byPitch = {}
	;(interventions || []).forEach(intervention => {
		const id = intervention.location_id ? String(intervention.location_id) : null
		if (!id)
			return ;
		if (!byPitch[id])
			byPitch[id] = []
		byPitch[id].push(intervention)
	})
	return byPitch
}

export function openCount(list)
{
	return (list || []).filter(isOpenIntervention).length
}

// Espace commun : comme le module Interventions, un emplacement sans type mais avec un special_type
export function isCommonZone(pitch)
{
	return !!pitch && (pitch.type === null || pitch.type === undefined || pitch.type === '') && !!pitch.special_type
}

export function familyLabel(pitch)
{
	if (isCommonZone(pitch))
		return 'Espace commun'
	const kind = pitchKind(pitch)
	if (kind === 'rental')
		return 'Locatif'
	if (kind === 'owner')
		return 'Propriétaire'
	if (kind === 'seasonal')
		return 'Saisonnier'
	return 'Emplacement nu'
}

// Libellé court des tuiles, qui doit tenir sur une ligne (le libellé complet reste dans la fiche)
const SHORT_LABELS = { 'Espace commun': 'Commun', 'Emplacement nu': 'Nu' }

export function shortFamilyLabel(pitch)
{
	const label = familyLabel(pitch)
	return SHORT_LABELS[label] || label
}

export const COMMON_GROUP = 'espaces-communs'

// Groupes affichés : les zones dans l'ordre naturel, puis les espaces communs à part
export function locationGroups(pitches)
{
	const all = pitches || []
	const groups = tourZones(all.filter(pitch => !isCommonZone(pitch))).map(group => ({ key: group.zone || '-', zone: group.zone, label: group.label, pitches: group.pitches }))
	const commons = all.filter(isCommonZone)
	if (commons.length > 0)
		groups.push({ key: COMMON_GROUP, zone: COMMON_GROUP, label: 'Espaces communs', pitches: [].concat(...tourZones(commons).map(group => group.pitches)) })
	return groups
}

// Fiche : les ouvertes d'abord, puis les plus récentes
export function sortInterventions(list)
{
	return (list || []).slice().sort((a, b) => (isOpenIntervention(b) ? 1 : 0) - (isOpenIntervention(a) ? 1 : 0) || (b.created_time || 0) - (a.created_time || 0))
}

// assigned_to : { type, id, name } dans le module Interventions (parfois un tableau dans les anciennes)
export function assigneeName(intervention)
{
	const value = Array.isArray(intervention.assigned_to) ? intervention.assigned_to[0] : intervention.assigned_to
	return value && value.name ? value.name : ''
}

export const URGENCY_LABELS = { IMPORTANT: 'Importante', CRITICAL: 'Urgente' }

// Inventaire d'un vacancier : objets comptés en dessous du modèle (count < quantity)
export function guestInventorySummary(inventory)
{
	const missing = []
	;(inventory.inventory || []).forEach(theme => {
		(theme.objects || []).forEach(object => {
			const count = parseInt(object.count, 10)
			const quantity = parseInt(object.quantity, 10)
			if (!isNaN(count) && !isNaN(quantity) && count < quantity)
				missing.push({ name: (object.name || '').trim(), count, quantity, theme: (theme.name || '').trim() })
		})
	})
	let title = 'Inventaire'
	if (inventory.type === 'arrival')
		title = 'Inventaire d\'entrée'
	else if (inventory.type === 'departure')
		title = 'Inventaire de sortie'
	return { title, missing }
}

// Séjour en cours et séjour à venir (les annulés sont ignorés)
export function currentAndNextStay(stays, now)
{
	const list = (stays || []).filter(stay => stay.status !== 'canceled' && stay.arrival_time && stay.departure_time)
	const current = list.find(stay => stay.arrival_time <= now && stay.departure_time >= now) || null
	const next = list.filter(stay => stay.arrival_time > now).sort((a, b) => a.arrival_time - b.arrival_time)[0] || null
	return { current, next }
}

// Séjours déjà commencés, du plus récent au plus ancien : ceux dont on lit les inventaires
export function startedStays(stays, now, limit = 8)
{
	return (stays || [])
		.filter(stay => stay.status !== 'canceled' && stay.arrival_time && stay.arrival_time <= now)
		.sort((a, b) => b.arrival_time - a.arrival_time)
		.slice(0, limit)
}

export const CHRONOLOGY_FILTERS = [
	{ id: 'all', label: 'Tout' },
	{ id: 'intervention', label: 'Interventions' },
	{ id: 'inventory', label: 'Inventaires' },
	{ id: 'tour', label: 'Tour' }
]

// Chronologie unique de l'emplacement (décision 8), la plus récente en haut :
// [{ id, kind: 'intervention' | 'inventory' | 'tour', time, label, meta }]
// tourLines : reportLines() du tour en cours (avec time) ; formatWhen(seconds) : « 21 oct. à 10:42 »
export function chronology({ interventions, inventories, tourLines, tourName, formatWhen })
{
	const events = []
	;(interventions || []).forEach(item => {
		if (!item.created_time)
			return ;
		const assignee = assigneeName(item)
		events.push({
			id: 'intervention-' + (item._id || item.created_time),
			kind: 'intervention',
			time: item.created_time,
			label: 'Intervention ' + (item.type ? item.type + ' ' : '') + 'créée : ' + (item.label || item.description),
			meta: [formatWhen(item.created_time), assignee, 'statut actuel : ' + (STATUS_LABELS[item.status] || item.status).toLowerCase()].filter(Boolean).join(', ')
		})
	})
	;(inventories || []).forEach((inventory, index) => {
		if (!inventory.create_time)
			return ;
		const summary = guestInventorySummary(inventory)
		const missing = summary.missing.length
		events.push({
			id: 'inventory-' + (inventory._id || inventory.create_time + '-' + index),
			kind: 'inventory',
			time: inventory.create_time,
			label: summary.title + ' par le vacancier' + (missing > 0 ? ' : ' + missing + (missing > 1 ? ' objets manquants' : ' objet manquant') : ' : rien de manquant'),
			meta: [formatWhen(inventory.create_time), inventory.stay ? 'séjour n° ' + inventory.stay.stay_number : null].filter(Boolean).join(', ')
		})
	})
	;(tourLines || []).forEach(line => {
		events.push({ id: 'tour-' + line.id, kind: 'tour', time: line.time || 0, label: line.label, meta: (tourName ? tourName + ', ' : '') + line.meta })
	})
	return events.sort((a, b) => b.time - a.time)
}

export function filterChronology(events, filter)
{
	return filter === 'all' ? events : events.filter(event => event.kind === filter)
}

// Fiche D5 : lignes regroupées par séjour (« Séjour 3488, du 15 au 22 août »), « Hors séjour » à la fin ;
// les groupes qui ont une intervention ouverte d'abord, puis du plus récent au plus ancien.
// items : [{ time, stayNumber, open }] ; formatRange(arrivée, départ) fourni par l'écran
export function groupByStay(items, stays, formatRange)
{
	const byNumber = {}
	;(stays || []).forEach(stay => {
		byNumber[String(stay.stay_number)] = stay
	})
	const groups = {}
	const keys = []
	items.forEach(item => {
		const key = item.stayNumber !== null && item.stayNumber !== undefined && byNumber[String(item.stayNumber)] ? String(item.stayNumber) : 'none'
		if (!groups[key])
		{
			groups[key] = []
			keys.push(key)
		}
		groups[key].push(item)
	})
	const latest = (key) => Math.max(...groups[key].map(item => item.time || 0))
	const anyOpen = (key) => (groups[key].some(item => item.open) ? 1 : 0)
	keys.sort((a, b) => (anyOpen(b) - anyOpen(a)) || ((a === 'none' ? 1 : 0) - (b === 'none' ? 1 : 0)) || (latest(b) - latest(a)))
	return keys.map(key => {
		const stay = byNumber[key]
		return {
			key,
			title: stay ? 'Séjour ' + stay.stay_number + ', ' + formatRange(stay.arrival_time, stay.departure_time) : 'Hors séjour',
			items: groups[key].slice().sort((a, b) => ((b.open ? 1 : 0) - (a.open ? 1 : 0)) || ((b.time || 0) - (a.time || 0)))
		}
	})
}

// Saisons présentes (années), de la plus récente à la plus ancienne : le filtre n'apparaît qu'à partir de deux
export function seasonsOf(items)
{
	const years = []
	items.forEach(item => {
		const year = item.time ? new Date(item.time * 1000).getFullYear() : null
		if (year && !years.includes(year))
			years.push(year)
	})
	return years.sort((a, b) => b - a)
}

export function inSeason(item, season)
{
	return season === 'all' || (!!item.time && new Date(item.time * 1000).getFullYear() === Number(season))
}

// Export Excel (demande d'Alexandra du 25/10/2026) : trois onglets en tableaux de lignes, pour xlsx.
// pitches : les emplacements affichés (zone ou recherche en cours) ; tour : tour en cours ou null.
// Les dates sont des objets Date, pour qu'Excel puisse les trier.
export function exportSheets({ pitches, byPitch, tour, user })
{
	const toDate = (seconds) => (seconds ? new Date(seconds * 1000) : '')
	const zoneOfPitch = (pitch) => (isCommonZone(pitch) ? 'Espaces communs' : ((pitch.zone || '').trim() || 'Sans zone'))
	const tourEntriesOf = (pitch) => (tour ? entriesAt(tour, pitch.location) : [])
	const tourStateOf = (pitch) => (tour ? TOUR_STATES[stateOfEntries(tourEntriesOf(pitch))].label : '')

	const locations = [['Emplacement', 'Zone', 'Type', 'Catégorie', 'Capacité', 'Interventions ouvertes', 'Interventions au total'].concat(tour ? ['État dans le ' + tour.name] : [])]
	const interventions = [['Emplacement', 'Zone', 'Type', 'Titre', 'Description', 'Statut', 'Urgence', 'Attribuée à', 'Créée le', 'Séjour']]
	const tourRows = [['Emplacement', 'Zone', 'État dans le tour', 'Action', 'Détail', 'Par', 'Le']]
	const URGENCIES = { NORMAL: 'Normale', IMPORTANT: 'Importante', CRITICAL: 'Urgente' }

	pitches.forEach(pitch => {
		const list = sortInterventions(byPitch[String(pitch._id)])
		locations.push([pitch.location, zoneOfPitch(pitch), familyLabel(pitch), (pitch.location_category || '').trim(), pitch.capacity || '', openCount(list), list.length].concat(tour ? [tourStateOf(pitch)] : []))
		list.forEach(item => {
			interventions.push([pitch.location, zoneOfPitch(pitch), item.type || '', item.label || '', item.description || '', STATUS_LABELS[item.status] || item.status || '', URGENCIES[item.urgency] || '', assigneeName(item), toDate(item.created_time), item.stay_number || ''])
		})
		tourEntriesOf(pitch).slice().sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0)).forEach(entry => {
			let action = 'Inventaire'
			let detail = entry.same ? 'Identique au dernier inventaire' : (entry.gaps ? entry.gaps + (entry.gaps > 1 ? ' écarts' : ' écart') : 'Aucun écart')
			if (entry.kind === 'ok')
			{
				action = 'Rien à signaler'
				detail = 'Inspection seule'
			}
			else if (entry.kind === 'comment')
			{
				action = 'Commentaire'
				detail = entry.comment ? entry.comment.comment.trim() : ''
			}
			else if (entry.kind === 'photo')
			{
				action = 'Photo'
				detail = entry.photo && entry.photo.description ? entry.photo.description : 'Sans description'
			}
			else if (entry.kind === 'intervention')
			{
				action = 'Intervention ' + (entry.type || '')
				detail = entry.description || entry.label || ''
			}
			const mine = !!user && entry.user === user._id
			tourRows.push([pitch.location, zoneOfPitch(pitch), tourStateOf(pitch), action.trim(), detail, entry.user_name || (mine ? 'Vous' : 'Un collègue'), toDate(entry.timestamp)])
		})
	})

	const sheets = [{ name: 'Emplacements', rows: locations }, { name: 'Interventions', rows: interventions }]
	if (tour)
		sheets.push({ name: 'Tour de terrain', rows: tourRows })
	return sheets
}
